import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, IconButton,
  MenuItem, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { EmailAccount, EmailMsg, sendEmail } from "./emailHubApi";
import EmailComposerToolbar from "../../Components/EmailComposer/EmailComposerToolbar";
import AttachmentDropzone from "../../Components/EmailComposer/AttachmentDropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Quill represents an empty editor as "<p><br></p>" — strip tags before
// length-checking so the Send button doesn't enable on an empty draft.
const isBodyEmpty = (html: string): boolean =>
  !html || html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim().length === 0;

const T = {
  navy: "#16233A", gold: "#7C4DFF", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f4f2ee",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  blue: "#1e40af", blueBg: "#eff6ff",
};

interface Props {
  open: boolean; onClose: () => void; accounts: EmailAccount[];
  replyTo?: EmailMsg | null; threadId?: number | null;
  initialBody?: string;
  /** Pre-fill the To field for first-touch outbound (Call/Email
   *  action bars across the CRM). Ignored when `replyTo` is set —
   *  reply mode owns the To value. */
  initialTo?: string;
  /** Pre-fill the subject. Same precedence as initialTo. */
  initialSubject?: string;
  /** CRM linkage forwarded onto the persisted email row. Either / both
   *  can be omitted — the backend will infer customer_id from
   *  lead.customer_id when only lead_id is supplied. */
  customerId?: number | null;
  leadId?: number | null;
  onSent: () => void;
}

const ComposeModal: React.FC<Props> = ({ open, onClose, accounts, replyTo, threadId, initialBody, initialTo, initialSubject, customerId, leadId, onSent }) => {
  const [accountId, setAccountId] = useState<number | "">("");
  const [to, setTo] = useState(""); const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(""); const [body, setBody] = useState("");
  const [sending, setSending] = useState(false); const [showCc, setShowCc] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  /* Tracking — populated by the shared toolbar. Wiring them onto the
     send call lets analytics + the timeline label "Sent using {template}"
     and the "AI-assisted" flag flow through to the backend. */
  const [templateId, setTemplateId]   = useState<number | null>(null);
  const [aiAssisted, setAiAssisted]   = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      if (replyTo) {
        setTo(replyTo.from_email || "");
        setSubject(replyTo.subject.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`);
        setBody(initialBody || "");
        setAccountId(replyTo.account_id || (accounts[0]?.id ?? ""));
      } else {
        setTo(initialTo || "");
        setSubject(initialSubject || "");
        setBody(initialBody || "");
        setAccountId(accounts[0]?.id ?? "");
      }
      setCc(""); setShowCc(false); setAttachments([]);
      setTemplateId(null); setAiAssisted(false);
    }
  }, [open, replyTo, accounts, initialBody, initialTo, initialSubject]);

  // True when body has any non-empty text. Drives the
  // "Replace existing body?" confirmation in the shared modals.
  const hasExistingBody = !isBodyEmpty(body);

  const handleSend = async () => {
    if (!accountId || !to.trim() || !subject.trim() || isBodyEmpty(body)) return;
    setSending(true);
    try {
      await sendEmail({
        account_id: accountId as number,
        to: to.split(",").map(e => e.trim()).filter(Boolean),
        cc: cc ? cc.split(",").map(e => e.trim()).filter(Boolean) : undefined,
        subject,
        // Quill always emits HTML, and the backend normalizer wraps any
        // plain-text input in <p> + nl2br — no client-side massaging
        // needed before send.
        body,
        thread_id: threadId || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        template_id: templateId,
        ai_assisted: aiAssisted,
        customer_id: customerId ?? undefined,
        lead_id: leadId ?? undefined,
      });
      onSent();
    } catch (e) { console.error("Send failed", e); }
    setSending(false);
  };

  return (
    <Dialog
      open={open}
      // Email composition is high-stakes — never let MUI auto-close
      // this Dialog. Both backdropClick AND escapeKeyDown can be
      // falsely triggered by OS-level dialogs interacting with the
      // browser focus model:
      //
      //   • Backdrop click — focus shift when the OS file picker
      //     surrenders focus on open / regains it on close is
      //     occasionally mis-detected as a click on the MuiBackdrop.
      //   • Escape key — some browsers / window managers re-fire the
      //     Esc key that dismissed the file picker onto the page,
      //     and MUI then treats it as the user closing the Dialog.
      //
      // We intentionally swallow the Dialog's own onClose. The X
      // button, Discard button, and the onSent close-on-success path
      // all call the `onClose` prop directly, so the Dialog still
      // has three perfectly good close affordances — none of which
      // can fire by accident.
      onClose={() => { /* no-op: see comment above */ }}
      disableEscapeKeyDown
      maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "85vh" } }}>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: T.navy, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white }}>{replyTo ? "Reply" : "New Message"}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* From */}
        <TextField select size="small" label="From" value={accountId} onChange={e => setAccountId(Number(e.target.value))}
          sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }}>
          {accounts.map(a => <MenuItem key={a.id} value={a.id}><Chip label={a.email.split("@")[0]} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.blueBg, color: T.blue, mr: 1 }} />{a.email}</MenuItem>)}
        </TextField>

        {/* To + Cc */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField fullWidth size="small" label="To" value={to} onChange={e => setTo(e.target.value)} placeholder="email@example.com"
            sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }} />
          {!showCc && <Button size="small" onClick={() => setShowCc(true)} sx={{ textTransform: "none", fontSize: 11, color: T.slateLt, whiteSpace: "nowrap" }}>Cc</Button>}
        </Box>
        {showCc && <TextField fullWidth size="small" label="Cc" value={cc} onChange={e => setCc(e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }} />}

        {/* Subject */}
        <TextField fullWidth size="small" label="Subject" value={subject} onChange={e => setSubject(e.target.value)}
          sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }} />

        {/* Shared composer toolbar — Use Template, AI Draft, quick chips.
            Lives above the body in BOTH this modal and the Lead → Send
            Email panel so the experience reads identically. */}
        <EmailComposerToolbar
          threadId={threadId ?? null}
          subject={subject}
          hasExistingBody={hasExistingBody}
          onApplyTemplate={(applied) => {
            setSubject(applied.subject);
            setBody(applied.body);
            setTemplateId(applied.template_id);
            setAiAssisted(false);
          }}
          onApplyAIDraft={(result) => {
            setBody(result.body);
            setTemplateId(null);
            setAiAssisted(true);
          }}
        />

        {/* Body — rich text. Quill ensures the rep sees what the
            customer will see (so template HTML renders instead of
            appearing as visible <p> tags) and emits clean HTML on
            send. Backend normalizer covers any plain-text edge case. */}
        <Box sx={{
          "& .ql-toolbar": { borderTopLeftRadius: 8, borderTopRightRadius: 8, borderColor: T.line, bgcolor: "#fafbfc" },
          "& .ql-container": { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, borderColor: T.line, fontSize: 13, fontFamily: "inherit", minHeight: 220 },
          "& .ql-editor": { minHeight: 220, lineHeight: 1.7 },
        }}>
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            placeholder="Write your message..."
          />
        </Box>

        {/* Active tracking labels — surfaces what'll be persisted on send
            so reps can see the template / AI flag at a glance. */}
        {(templateId || aiAssisted) && (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {templateId && (
              <Chip size="small" label="Template applied" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: T.goldLight, color: T.navy }} />
            )}
            {aiAssisted && (
              <Chip size="small" label="AI-assisted" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#F5EFE0", color: "#A8893E" }} />
            )}
          </Box>
        )}

        {replyTo && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: T.bg, border: `1px solid ${T.line}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, mb: 0.5 }}>Replying to {replyTo.from_name || replyTo.from_email}</Typography>
            <Typography sx={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }} noWrap>{replyTo.body_preview}</Typography>
          </Box>
        )}

        {/* Shared drag-and-drop attachment area. */}
        <AttachmentDropzone
          files={attachments}
          onChange={setAttachments}
          maxFiles={10}
          maxBytesEach={25 * 1024 * 1024}
        />
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 12, color: T.slate }}>Discard</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending || !to.trim() || !subject.trim()}
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: T.navy, borderRadius: 2, px: 3, "&:hover": { bgcolor: T.gold, color: T.navy } }}>
          {sending ? "Sending..." : "Send"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ComposeModal;
